//libs\featureds\public\clients\src\lib\components\auth-client\auth-client.component.ts
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { tap, switchMap, finalize, distinctUntilChanged } from 'rxjs';
import { ClientAuthService } from '../../data-access/client-auth.service';
import { ClientsService } from '../../data-access/clients.service';
import { ClientAuthSession, AuthenticatedClient } from '../../models/client.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import type { MascotState } from '@cardapio-online/mascot';


export type AuthClientMode = 'lookup' | 'login' | 'register' | 'identified';

export interface AuthClientState {
  mode: AuthClientMode;
  title: string;
  description: string;
  submitLabel: string;
  submitting: boolean;
  lookingUp: boolean;
}
export type AuthClientMascotChange = Partial<MascotState>;

export const AUTH_CLIENT_INITIAL_STATE: AuthClientState = {
  mode: 'lookup',
  title: 'Identifique-se',
  description: 'Informe seu telefone.',
  submitLabel: 'Continuar',
  submitting: false,
  lookingUp: false,
};


@Component({
  selector: 'lib-auth-client',
  templateUrl: './auth-client.component.html',
  styleUrl: './auth-client.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthClientComponent  implements OnInit {
  private readonly clientsService = inject(ClientsService);
  private readonly clientAuthService = inject(ClientAuthService);
  private readonly destroyRef = inject(DestroyRef);

readonly lookupOnPhoneBlur = input<boolean>(false);

readonly stateChanged = output<AuthClientState>();
readonly mascotChanged = output<AuthClientMascotChange>();
readonly authenticated = output<ClientAuthSession>();


  protected readonly phoneControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(11)],
  });

  protected readonly mode = signal<AuthClientMode>('lookup');
  protected readonly submitting = signal(false);
  protected readonly lookingUp = signal(false);
  protected readonly clientFound = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly message = signal<string | null>(null);

  protected readonly customerName = signal('');
  protected readonly customerEmail = signal('');
  protected readonly birthDateInput = signal('');
  protected readonly phonePreview = signal('Informe seu telefone para comecar');

  private readonly selectedClient = signal<AuthenticatedClient | null>(null);
  private lastLookupPhone = '';

  protected readonly title = computed(() => {
    switch (this.mode()) {
      case 'identified':
        return 'Cliente identificado';
      case 'login':
        return 'Entrar para continuar';
      case 'register':
        return 'Cadastrar cliente';
      default:
        return 'Identifique-se';
    }
  });

  protected readonly description = computed(() => {
    switch (this.mode()) {
      case 'identified':
        return 'Seu acesso de cliente ja esta ativo neste dispositivo.';
      case 'login':
        return 'Encontramos seu cadastro. Confirme sua data de nascimento para seguir.';
      case 'register':
        return 'Nao achamos esse telefone. Complete seu cadastro para continuar.';
      default:
        return 'Insira seu whatsapp para prosseguir com seu pedido.';
    }
  });

  protected readonly submitLabel = computed(() => {
    switch (this.mode()) {
      case 'identified':
        return 'Fechar';
      case 'login':
        return this.submitting() ? 'Entrando...' : 'Entrar';
      case 'register':
        return this.submitting() ? 'Cadastrando...' : 'Cadastrar';
      default:
        return 'Continuar';
    }
  });

  private readonly stateEffect = effect(() => {
    this.stateChanged.emit({
      mode: this.mode(),
      title: this.title(),
      description: this.description(),
      submitLabel: this.submitLabel(),
      submitting: this.submitting(),
      lookingUp: this.lookingUp(),
    });
  });

ngOnInit(): void {
  this.watchPhoneChanges();
this.emitMascot({
  thoughtEmoji: '😊',
  thoughtText: 'Que bom ter você aqui',
  thoughtVisible: false,
  eyes: 'happy',
  animation: 'float',
});
  const session = this.clientAuthService.getSessionSnapshot();

  if (!session?.token || !session.client?.id) {
    return;
  }

  this.applyClient(session.client);
  this.mode.set('identified');
  this.message.set(`Voce ja esta identificado como ${session.client.name}.`);
  this.emitMascot({
  thoughtEmoji: '👋',
  thoughtText: `Que bom ter você novamente aqui, ${session.client.name}`,
  thoughtVisible: true,
  eyes: 'happy',
  animation: 'bounce',
});
}

public reset(): void {
  this.phoneControl.reset('', { emitEvent: false });

  this.mode.set('lookup');
  this.submitting.set(false);
  this.lookingUp.set(false);
  this.clientFound.set(false);
  this.error.set(null);
  this.message.set(null);
  this.customerName.set('');
  this.customerEmail.set('');
  this.birthDateInput.set('');
  this.phonePreview.set('Informe seu telefone para comecar');
  this.selectedClient.set(null);

  this.lastLookupPhone = '';
}

protected onPhoneBlur(): void {
  if (!this.lookupOnPhoneBlur()) {
    return;
  }

  const phone = this.getPhoneValue();

  if (phone.length !== 11) {
    return;
  }

  this.lookupClientByPhone(phone);
}

public submit(): void {
    if (this.submitting()) {
      return;
    }

    if (this.mode() === 'identified') {
      this.emitCurrentSession();
      return;
    }

    const phone = this.getPhoneValue();

    this.syncPhoneControl(phone);
    this.error.set(null);
    this.message.set(null);

    if (phone.length !== 11) {
      this.phoneControl.markAsTouched();
      this.error.set('Informe um telefone valido com 11 digitos.');
      return;
    }

    if (this.mode() === 'lookup') {
      this.lookupClientByPhone(phone);
      return;
    }

    const dateBirth = this.toDateBirthValue(this.birthDateInput());

    if (!dateBirth) {
      this.error.set('Escolha sua data de nascimento para continuar.');
      return;
    }

    if (this.mode() === 'register' && this.customerName().trim().length === 0) {
      this.error.set('Informe seu nome para concluir o cadastro.');
      return;
    }

    this.submitting.set(true);

    const request$ =
      this.mode() === 'login'
        ? this.clientAuthService.login(
            { phone, dateBirth },
            {
              id: this.selectedClient()?.id ?? '',
              name: this.customerName().trim(),
              phone,
              email: this.customerEmail().trim() || null,
            }
          )
        : this.clientsService
            .create({
              name: this.customerName().trim(),
              phone,
              email: this.customerEmail().trim() || null,
              dateBirth,
            })
            .pipe(
              tap((response) => {
                this.selectedClient.set(response.data);
                this.applyClient(response.data);
              }),
              switchMap((response) =>
                this.clientAuthService.login(
                  { phone, dateBirth },
                  {
                    id: response.data.id,
                    name: response.data.name,
                    phone: response.data.phone,
                    email: response.data.email ?? null,
                  }
                )
              )
            );

    request$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.submitting.set(false);
        })
      )
      .subscribe({
        next: () => {
          const session = this.clientAuthService.getSessionSnapshot();

          if (!session) {
            this.error.set('Sessao de cliente ausente apos autenticacao.');
            return;
          }

          this.applyClient(session.client);
          this.mode.set('identified');
          this.message.set('Tudo certo. Seu cliente foi identificado.');
          this.authenticated.emit(session);
        },
        error: (error: HttpErrorResponse) => {
          this.error.set(this.resolveAuthErrorMessage(error));
        },
      });
  }

private watchPhoneChanges(): void {
  this.phoneControl.valueChanges
    .pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe((value) => {
      const phone = this.extractDigits(value).slice(0, 11);

      this.phonePreview.set(
        phone
          ? `Informe seu telefone para comecar: ${this.maskPhone(phone)}`
          : 'Informe seu telefone para comecar'
      );

      if (!phone || phone === this.lastLookupPhone) {
        return;
      }

      this.lastLookupPhone = '';
      this.selectedClient.set(null);
      this.clientFound.set(false);
      this.customerName.set('');
      this.customerEmail.set('');
      this.birthDateInput.set('');
      this.error.set(null);
      this.message.set(null);
      this.mode.set('lookup');
    });
}

private lookupClientByPhone(phone: string): void {
  this.syncPhoneControl(phone);
  this.error.set(null);

  if (phone.length !== 11) {
    this.mode.set('lookup');
    this.clientFound.set(false);
    this.message.set('Digite um telefone com 11 digitos para continuar.');
    return;
  }

  if (this.lastLookupPhone === phone && this.mode() !== 'lookup') {
    return;
  }

  this.lastLookupPhone = phone;
  this.message.set(null);
  this.lookingUp.set(true);

  this.emitMascot({
  thoughtEmoji: '🔎',
  thoughtText: 'Vou procurar seu cadastro',
  thoughtVisible: true,
  eyes: 'looking-down',
  animation: 'float',
});

  this.clientsService
    .getByPhone(phone)
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.lookingUp.set(false);
      })
    )
    .subscribe({
      next: (response) => {
        this.selectedClient.set(response.data);
        this.applyClient(response.data);
        this.clientFound.set(true);
        this.mode.set('login');
        this.message.set(`Cadastro encontrado para ${response.data.name}.`);
        this.emitMascot({
  thoughtEmoji: '👋',
  thoughtText: `Bem vindo de volta, ${response.data.name}`,
  thoughtVisible: true,
  eyes: 'happy',
  animation: 'bounce',
});
      },
      error: (error: HttpErrorResponse) => {
        this.selectedClient.set(null);
        this.clientFound.set(false);
        this.customerName.set('');
        this.customerEmail.set('');

        if (error.status === 404) {
          this.emitMascot({
  thoughtEmoji: '📝',
  thoughtText: 'Vamos criar seu cadastro rapidinho',
  thoughtVisible: true,
  eyes: 'excited',
  animation: 'float',
});
          this.mode.set('register');
          this.message.set(
            'Esse telefone ainda nao tem cadastro. Preencha seus dados para continuar.'
          );
          return;
        }

        this.mode.set('lookup');
        this.error.set(
          'Nao conseguimos consultar esse telefone agora. Tente novamente em instantes.'
        );
        this.emitMascot({
  thoughtEmoji: '⚠️',
  thoughtText: 'Ops, tive dificuldade para consultar agora',
  thoughtVisible: true,
  eyes: 'sleepy',
  animation: 'shake',
});
      },
    });
}
  private applyClient(client: AuthenticatedClient): void {
    this.selectedClient.set(client);
    this.customerName.set(client.name);
    this.customerEmail.set(client.email ?? '');
    this.syncPhoneControl(client.phone);
  }

  private emitCurrentSession(): void {
    const session = this.clientAuthService.getSessionSnapshot();

    if (!session) {
      this.mode.set('lookup');
      this.error.set('Identifique-se novamente para continuar.');
      return;
    }

    this.authenticated.emit(session);
  }

  private getPhoneValue(): string {
    return this.extractDigits(this.phoneControl.value).slice(0, 11);
  }

private syncPhoneControl(value: string): void {
  const phone = this.extractDigits(value).slice(0, 11);

  this.phoneControl.setValue(phone, { emitEvent: false });
  this.phonePreview.set(
    phone
      ? `Informe seu telefone para comecar: ${this.maskPhone(phone)}`
      : 'Informe seu telefone para comecar'
  );
}

  private extractDigits(value: unknown): string {
    return String(value ?? '').replace(/\D/g, '');
  }

  private maskPhone(value: string): string {
    const phone = this.extractDigits(value).slice(0, 11);

    if (phone.length <= 2) {
      return phone;
    }

    if (phone.length <= 6) {
      return `(${phone.slice(0, 2)}) ${phone.slice(2)}`;
    }

    if (phone.length <= 10) {
      return `(${phone.slice(0, 2)}) ${phone.slice(2, 6)}-${phone.slice(6)}`;
    }

    return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7, 11)}`;
  }

  private toDateBirthValue(value: string): string | null {
    const normalized = String(value ?? '').trim();

    if (!normalized) {
      return null;
    }

    const [year, month, day] = normalized.split('-');

    if (!year || !month || !day) {
      return null;
    }

    return `${day}${month}`;
  }
private emitMascot(change: AuthClientMascotChange): void {
  this.mascotChanged.emit(change);
}
  private resolveAuthErrorMessage(error: HttpErrorResponse): string {
    const code = String(error?.error?.code ?? '');

    if (code === 'INVALID_CREDENTIALS') {
      return 'Data de nascimento invalida para esse telefone.';
    }

    if (code === 'DATE_BIRTH_NOT_SET') {
      return 'Seu cadastro nao tem data de nascimento registrada. Faca um novo cadastro com outro telefone ou fale com a loja.';
    }

    if (code === 'CONFLICT') {
      return 'Esse telefone ou e-mail ja esta cadastrado.';
    }

    if (error.status === 429) {
      return 'Voce fez muitas tentativas seguidas. Aguarde um pouco e tente novamente.';
    }

    return 'Nao foi possivel concluir essa etapa agora. Tente novamente em instantes.';
  }
}